import { Alert, Box, Button, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useCountdown } from '../hooks/useCountdown';
import { usePlaceBet } from '../hooks/useMarkets';
import { formatCountdown, formatTokens } from '../lib/format';
import { enqueueBet } from '../lib/betQueue';
import { newIdempotencyKey } from '../lib/idempotency';
import { MINIMUM_STAKE, type Market } from '../schemas/market.schema';
import { ApiError, getErrorMessage } from '../services/api-client';
import { colors, mono } from '../themes/theme';
import { OutcomeChoice } from './OutcomeChoice';

interface BetSlipProps {
    market: Market;
    balance: number;
    eliminated: boolean;
}

/**
 * Room left under the per-market ceiling.
 *
 * Shown rather than enforced silently: a Max button that stops short of your
 * balance with no explanation reads as a bug.
 */
const roomLeft = (market: Market, balance: number): number =>
    Math.max(0, Math.min(balance, market.maxStakePerMarket - market.myStake));

const QUICK_STAKES = [100, 500, 1000] as const;

export function BetSlip({ market, balance, eliminated }: BetSlipProps) {
    const [outcomeId, setOutcomeId] = useState<number | null>(null);
    const [guess, setGuess] = useState('');
    const [stake, setStake] = useState(0);
    // Held across retries: a fresh key on each attempt would let a retry place
    // a second bet, which is the whole thing the key exists to prevent.
    const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
    const [queued, setQueued] = useState(false);

    const placeBet = usePlaceBet();
    const remaining = useCountdown(market.closesAt);
    const isClosest = market.kind === 'closest';
    const room = roomLeft(market, balance);

    const guessNumber = Number(guess);
    const guessValid = isClosest && guess.trim() !== '' && Number.isFinite(guessNumber);

    const ready =
        stake >= MINIMUM_STAKE &&
        stake <= room &&
        (isClosest ? guessValid : outcomeId !== null);

    /**
     * What this stake would return if the market settled right now.
     *
     * Shown because a pari-mutuel payout is not fixed when you bet -- later
     * money dilutes it -- and nobody should feel tricked by arithmetic they
     * never saw.
     */
    const wouldReturn = useMemo(() => {
        if (isClosest) return market.pot + stake;
        const chosen = market.outcomes.find((o) => o.id === outcomeId);
        if (!chosen || stake <= 0) return 0;
        const winningPool = chosen.staked + stake;
        return Math.floor((stake * (market.pot + stake)) / winningPool);
    }, [isClosest, market.outcomes, market.pot, outcomeId, stake]);

    if (market.status !== 'open') {
        return (
            <Alert severity="info" sx={{ mt: 2 }}>
                Betting is closed on this one.
            </Alert>
        );
    }

    if (eliminated) {
        return (
            <Alert severity="warning" sx={{ mt: 2 }}>
                You&rsquo;re out for the night, so you can&rsquo;t back this one — but you can
                still argue about it below.
            </Alert>
        );
    }

    if (balance < MINIMUM_STAKE) {
        return (
            <Alert severity="warning" sx={{ mt: 2 }}>
                You need at least {MINIMUM_STAKE} tokens to bet.
            </Alert>
        );
    }

    if (room < MINIMUM_STAKE) {
        return (
            <Alert severity="info" sx={{ mt: 2 }}>
                You&rsquo;re in for {formatTokens(market.myStake)} — the most anyone can put on
                one bet. Everybody gets a fair go at the pot.
            </Alert>
        );
    }

    return (
        <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography
                sx={{
                    fontFamily: mono,
                    fontSize: '0.68rem',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: remaining < 30_000 ? colors.loss : colors.safety,
                    fontVariantNumeric: 'tabular-nums',
                }}
            >
                {remaining <= 0 ? 'Closing…' : `Closes in ${formatCountdown(remaining)}`}
            </Typography>

            {isClosest ? (
                <TextField
                    label="Your guess"
                    value={guess}
                    onChange={(event) => setGuess(event.target.value.replace(/[^\d.-]/g, ''))}
                    type="tel"
                    inputMode="decimal"
                    helperText="Whoever's closest takes the whole pot"
                />
            ) : (
                <OutcomeChoice
                    market={market}
                    selectedId={outcomeId}
                    onSelect={setOutcomeId}
                />
            )}

            <Box>
                <ToggleButtonGroup
                    value={stake}
                    exclusive
                    onChange={(_event, value: number | null) => value !== null && setStake(value)}
                    sx={{
                        display: 'flex',
                        gap: 1,
                        '& .MuiToggleButton-root': {
                            flex: 1,
                            borderRadius: '3px !important',
                            border: `1px solid ${colors.line} !important`,
                            fontFamily: mono,
                        },
                    }}
                >
                    {QUICK_STAKES.filter((amount) => amount <= room).map((amount) => (
                        <ToggleButton key={amount} value={amount}>
                            {amount}
                        </ToggleButton>
                    ))}
                    <ToggleButton value={room}>All in</ToggleButton>
                </ToggleButtonGroup>

                <TextField
                    label="How much"
                    value={stake === 0 ? '' : String(stake)}
                    onChange={(event) => setStake(Number(event.target.value.replace(/\D/g, '')) || 0)}
                    type="tel"
                    inputMode="numeric"
                    sx={{ mt: 1 }}
                    error={stake > room}
                    helperText={
                        stake > room
                            ? room < balance
                                ? `Up to ${formatTokens(room)} on one bet`
                                : `You only have ${formatTokens(balance)}`
                            : `You have ${formatTokens(balance)} · up to ${formatTokens(room)} on this one`
                    }
                />
            </Box>

            {ready && (
                <Typography sx={{ fontFamily: mono, fontSize: '0.8rem', color: colors.win }}>
                    If you&rsquo;re right, you&rsquo;d get about {formatTokens(wouldReturn)} back
                </Typography>
            )}

            {placeBet.isError &&
                (queued ? (
                    <Alert severity="info">
                        No signal — this is saved and goes in as soon as you&rsquo;re back.
                    </Alert>
                ) : (
                    <Alert severity="error">{getErrorMessage(placeBet.error)}</Alert>
                ))}

            <Button
                variant="contained"
                size="large"
                disabled={!ready || placeBet.isPending}
                onClick={() =>
                    placeBet.mutate(
                        {
                            marketId: market.id,
                            outcomeId: isClosest ? undefined : (outcomeId ?? undefined),
                            guessValue: isClosest ? guessNumber : undefined,
                            stake,
                            idempotencyKey,
                        },
                        {
                            onSuccess: () => {
                                // A new key only after one bet has landed, so
                                // the next bet is a distinct wager.
                                setIdempotencyKey(newIdempotencyKey());
                                setStake(0);
                                setGuess('');
                                setOutcomeId(null);
                                setQueued(false);
                            },
                            onError: (error) => {
                                // A rejection the server actually made is final.
                                // Anything else never reached it, so the bet is
                                // held -- with its key, so the replay collapses
                                // into one wager rather than two.
                                const rejected =
                                    error instanceof ApiError &&
                                    error.status >= 400 &&
                                    error.status < 500;
                                if (rejected) return;

                                enqueueBet({
                                    marketId: market.id,
                                    outcomeId: isClosest ? undefined : (outcomeId ?? undefined),
                                    guessValue: isClosest ? guessNumber : undefined,
                                    stake,
                                    idempotencyKey,
                                });
                                setQueued(true);
                            },
                        },
                    )
                }
            >
                {placeBet.isPending
                    ? 'Placing…'
                    : stake > 0
                      ? `Put ${formatTokens(stake)} on it`
                      : 'Pick an amount'}
            </Button>
        </Stack>
    );
}
